import React, { useState, useEffect, useContext } from 'react';
import { Star, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { authContext } from 'contexts/AuthContext';

const FavrateLocation = ({ vendorId, embedded = false, onTimelineRefresh }) => {
  const [selectedState, setSelectedState] = useState(null);
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [statesLoading, setStatesLoading] = useState(true);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [statesError, setStatesError] = useState(null);
  const [error, setError] = useState(null);
  const { auth } = useContext(authContext);

  const fetchStatesAPI = async () => {
    if (!vendorId) return;
    setStatesLoading(true);
    try {
      const baseURL = process.env.REACT_APP_REST_API_ENDPOINT;
      const res = await axios.get(`${baseURL}trader/trader-states?traderId=${vendorId}&searchTerm=`, {
        headers: { Authorization: `Bearer ${auth?.data?.token}` },
      });
      const mappedStates = res?.data?.data || [];
      setAllStates(mappedStates);
      if (mappedStates.length > 0) {
        setSelectedState(mappedStates[0]);
      }
      setStatesLoading(false);
      setLoadingLocations(false);
    } catch (err) {
      setStatesError(err?.response?.data?.message || 'Failed to fetch states');
      setStatesLoading(false);
      setLoadingLocations(false);
    }
  };

  const fetchCitiesAPI = async (stateId) => {
    if (!vendorId || !stateId) return;
    setCitiesLoading(true);
    try {
      const baseURL = process.env.REACT_APP_REST_API_ENDPOINT;
      const res = await axios.get(`${baseURL}trader/trader-cities?traderId=${vendorId}&searchTerm=&stateId=${stateId}`, {
        headers: { Authorization: `Bearer ${auth?.data?.token}` },
      });
      const mappedCities = res?.data?.data || [];
      setAllCities(mappedCities);
      setCitiesLoading(false);
    } catch (err) {
      console.error(err);
      setAllCities([]);
      setCitiesLoading(false);
    }
  };

  useEffect(() => {
    fetchStatesAPI();
  }, [vendorId]);

  useEffect(() => {
    if (selectedState && selectedState.id) {
      fetchCitiesAPI(selectedState.id);
    }
  }, [selectedState]);

  // Toggle favorite for a State
  const toggleStateFavorite = async (stateId, isCurrentlyFavorite) => {
    // Optimistically update the state star
    setAllStates(prev =>
      prev.map(s => (s.id === stateId ? { ...s, isFavorite: !isCurrentlyFavorite } : s))
    );

    // If adding to favourite, optimistically mark all currently shown cities as favourite too
    if (!isCurrentlyFavorite && selectedState?.id === stateId) {
      setAllCities(prev => prev.map(c => ({ ...c, isFavorite: true })));
    }
    // If removing from favourite, optimistically un-mark all currently shown cities
    if (isCurrentlyFavorite && selectedState?.id === stateId) {
      setAllCities(prev => prev.map(c => ({ ...c, isFavorite: false })));
    }

    try {
      const baseURL = process.env.REACT_APP_REST_API_ENDPOINT;
      const currentVendorId = vendorId ? parseInt(vendorId, 10) : null;

      if (!currentVendorId) {
        console.error('Vendor ID not found in props');
        return;
      }

      const payload = {
        traderId: String(currentVendorId),
        stateId: parseInt(stateId, 10),
        addingToFavourite: !isCurrentlyFavorite,
      };
      const res = await axios.put(`${baseURL}trader/update/favourites-state`, payload, {
        headers: {
          Authorization: `Bearer ${auth?.data?.token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (res?.data?.message) {
        if (res.data.error) {
          toast.error(res.data.message);
          // Revert state star on error
          setAllStates(prev =>
            prev.map(s => (s.id === stateId ? { ...s, isFavorite: isCurrentlyFavorite } : s))
          );
          // Revert cities on error
          if (selectedState?.id === stateId) {
            fetchCitiesAPI(stateId);
          }
        } else {
          toast.success(res.data.message);
          // Re-fetch cities from API to get accurate favourite status from backend
          if (selectedState?.id === stateId) {
            fetchCitiesAPI(stateId);
          }
        }
      }

      if (!res?.data?.error && onTimelineRefresh) {
        onTimelineRefresh();
      }
    } catch (err) {
      console.error('Error toggling state favorite:', err);
      toast.error(err?.response?.data?.message || 'Something went wrong');
      // Revert on error
      setAllStates(prev =>
        prev.map(s => (s.id === stateId ? { ...s, isFavorite: isCurrentlyFavorite } : s))
      );
      if (selectedState?.id === stateId) {
        fetchCitiesAPI(stateId);
      }
    }
  };

  // Toggle favorite for a City
  const toggleCityFavorite = async (cityId, isCurrentlyFavorite) => {
    // Update local state optimistically
    setAllCities(prev =>
      prev.map(c => (c.id === cityId ? { ...c, isFavorite: !isCurrentlyFavorite } : c))
    );

    try {
      const baseURL = process.env.REACT_APP_REST_API_ENDPOINT;
      const currentVendorId = vendorId ? parseInt(vendorId, 10) : null;

      if (!currentVendorId) {
        console.error('Vendor ID not found in props');
        return;
      }

      const payload = {
        traderId: String(currentVendorId),
        cityId: parseInt(cityId, 10),
        addingToFavourite: !isCurrentlyFavorite,
      };
      const res = await axios.put(`${baseURL}trader/update/favourites-city`, payload, {
        headers: {
          Authorization: `Bearer ${auth?.data?.token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (res?.data?.message) {
        if (res.data.error) {
          toast.error(res.data.message);
          setAllCities(prev =>
            prev.map(c => (c.id === cityId ? { ...c, isFavorite: isCurrentlyFavorite } : c))
          );
        } else {
          toast.success(res.data.message);
        }
      }

      if (!res?.data?.error && onTimelineRefresh) {
        onTimelineRefresh();
      }
    } catch (err) {
      console.error('Error toggling city favorite:', err);
      toast.error(err?.response?.data?.message || 'Something went wrong');
      // Revert on error
      setAllCities(prev =>
        prev.map(c => (c.id === cityId ? { ...c, isFavorite: isCurrentlyFavorite } : c))
      );
    }
  };

  if (loadingLocations || statesLoading) {
    return (
      <div className={embedded ? '' : 'min-h-screen bg-gray-50 p-6'}>
        <div className="bg-white rounded-lg shadow-sm p-10 text-center text-gray-500">
          Loading locations...
        </div>
      </div>
    );
  }

  if (error || statesError) {
    return (
      <div className={embedded ? '' : 'min-h-screen bg-gray-50 p-6'}>
        <div className="bg-white rounded-lg shadow-sm p-10 text-center text-red-500">
          {error || 'Failed to load locations'}
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50 p-6'}>


      {/* Main Content - Side by Side Columns */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="flex border-b bg-gray-50 text-[16px]" style={{ display: 'flex' }}>
          <div className="w-1/3 p-3 border-r font-semibold text-gray-700" style={{ width: '35%', minWidth: '35%' }}>
            State
          </div>
          <div className="flex-1 p-3 font-semibold text-gray-700" style={{ paddingLeft: '90px' }}>
            City
          </div>
        </div>

        <div className="flex" style={{ display: 'flex' }}>
          {/* State Column - Dynamic */}
          <div
            className="w-1/3 border-r"
            style={{ width: '35%', minWidth: '35%', height: '500px', overflowY: 'auto' }}
          >
            {allStates.length > 0 ? (
              allStates.map(state => (
                <div
                  key={state.id}
                  onClick={() => setSelectedState(state)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')
                      setSelectedState(state);
                  }}
                  role="button"
                  tabIndex={0}
                  className="flex items-center justify-between px-4 py-3 cursor-pointer text-sm transition-colors border-b"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor:
                      selectedState?.id === state.id ? '#212529' : '#ffffff',
                    color:
                      selectedState?.id === state.id ? '#ffffff' : '#374151',
                    fontWeight: selectedState?.id === state.id ? '600' : '400',
                  }}
                >
                  <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <Star
                      className="w-4 h-4 cursor-pointer"
                      style={{
                        color: state.isFavorite ? '#FFD700' : '#9ca3af',
                        fill: state.isFavorite ? '#FFD700' : 'none',
                        flexShrink: 0,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        toggleStateFavorite(state.id, state.isFavorite);
                      }}
                    />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{state.name}</span>
                  </div>
                  <ChevronRight
                    className="w-4 h-4"
                    style={{
                      color:
                        selectedState?.id === state.id ? '#ffffff' : '#9ca3af',
                    }}
                  />
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-10">
                No states available
              </div>
            )}
          </div>

          {/* City Column - Dynamic */}
          <div
            className="flex-1 p-5"
            style={{ height: '500px', overflowY: 'auto', paddingLeft: '90px' }}
          >
            {citiesLoading ? (
              <div className="text-center text-gray-500 py-10">
                Loading cities...
              </div>
            ) : allCities.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {allCities.map(city => (
                  <div
                    key={city.id}
                    className="flex items-center gap-2 border border-gray-200 rounded-md p-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <Star
                      className="w-4 h-4 cursor-pointer"
                      style={{
                        color: city.isFavorite ? '#FFD700' : '#9ca3af',
                        fill: city.isFavorite ? '#FFD700' : 'none',
                      }}
                      onClick={() =>
                        toggleCityFavorite(city.id, city.isFavorite)
                      }
                    />
                    <span className="text-sm text-gray-700">
                      {city.name}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-10">
                No cities available for this state
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FavrateLocation;
